/**
 * Hook for managing session progress toast triggers
 * Shows toast after reading milestones (20min or 10 pages)
 * Uses 2:1 recency bias for concept selection with partial repeat fallback
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { TLDRData, getAllConcepts } from '../utils/conceptExtractor';

// Timer constants (PRODUCTION VALUES)
const TOAST_TIME_THRESHOLD_MS = 25 * 60 * 1000;  // 25 minutes
const TOAST_PAGES_THRESHOLD = 10;                 // 10 pages
const CHECK_INTERVAL_MS = 30 * 1000;              // Check every 30 seconds

export interface UseSessionProgressToastOptions {
  pagesVisited: number[];
  tldrCache: Map<number, TLDRData>;
  isReady: boolean;  // Only start tracking after book loads
  currentPage: number;  // Current page to exclude from concepts
}

export interface UseSessionProgressToastReturn {
  showToast: boolean;
  toastConcepts: string[];
  onRecap: () => string;      // Returns prompt to populate chat
  onConnect: () => string;    // Returns prompt to populate chat
  onDismiss: () => void;
}

// Fisher-Yates shuffle
function shuffle<T>(arr: T[]): T[] {
  const result = [...arr];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

// Get concepts from a list of pages
function getConceptsFromPages(
  pages: number[],
  tldrCache: Map<number, TLDRData>
): string[] {
  const concepts: string[] = [];
  for (const page of pages) {
    const data = tldrCache.get(page);
    if (data) {
      concepts.push(...getAllConcepts(data));
    }
  }
  return Array.from(new Set(concepts)); // Deduplicate
}

/**
 * Select concepts for toast with 2:1 recency bias
 * Recent pages get 2 concepts, older pages get 1
 * Falls back to partial repeats if fresh concepts exhausted
 */
function selectConceptsForToast(
  pagesVisited: number[],
  tldrCache: Map<number, TLDRData>,
  lastToastPageIndex: number,
  shownConcepts: Set<string>,
  currentPage: number
): string[] {
  // Exclude current page - user may not have finished reading it
  const eligiblePages = pagesVisited.filter(p => p !== currentPage);

  // Split pages into recent vs older
  const recentPages = eligiblePages.slice(Math.max(0, lastToastPageIndex - 1));
  const olderPages = eligiblePages.slice(0, Math.max(0, lastToastPageIndex - 1));

  // Get concepts from each pool
  const recentConcepts = getConceptsFromPages(recentPages, tldrCache);
  const olderConcepts = getConceptsFromPages(olderPages, tldrCache);

  // Get fresh concepts only (not already shown)
  const freshRecent = recentConcepts.filter(c => !shownConcepts.has(c));
  const freshOlder = olderConcepts.filter(c => !shownConcepts.has(c));

  const result: string[] = [];

  // Try 2 from recent (fresh first)
  const shuffledFreshRecent = shuffle(freshRecent);
  result.push(...shuffledFreshRecent.slice(0, 2));

  // Try 1 from older (fresh first)
  if (result.length < 3 && freshOlder.length > 0) {
    const shuffledFreshOlder = shuffle(freshOlder);
    result.push(shuffledFreshOlder[0]);
  }

  // If still < 3, fill from remaining fresh recent
  if (result.length < 3 && shuffledFreshRecent.length > 2) {
    result.push(...shuffledFreshRecent.slice(2, 3 - result.length + 2));
  }

  // No partial repeats - only show fresh concepts
  // If we have less than 3, that's fine - we show what we have
  // If we have 0, the toast won't be shown (handled in triggerToast)

  return result.slice(0, 3);
}

export function useSessionProgressToast({
  pagesVisited,
  tldrCache,
  isReady,
  currentPage
}: UseSessionProgressToastOptions): UseSessionProgressToastReturn {
  // State
  const [showToast, setShowToast] = useState(false);
  const [toastConcepts, setToastConcepts] = useState<string[]>([]);

  // Refs for tracking without re-renders
  const lastToastTimeRef = useRef<number>(0);
  const lastToastPageIndexRef = useRef<number>(0);
  const shownConceptsRef = useRef<Set<string>>(new Set());
  const wasReadyRef = useRef<boolean>(false);
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pagesVisitedRef = useRef<number[]>(pagesVisited);
  const tldrCacheRef = useRef<Map<number, TLDRData>>(tldrCache);
  const currentPageRef = useRef<number>(currentPage);

  // Keep refs in sync
  useEffect(() => {
    pagesVisitedRef.current = pagesVisited;
    tldrCacheRef.current = tldrCache;
    currentPageRef.current = currentPage;
  }, [pagesVisited, tldrCache, currentPage]);

  // Initialize timestamps when isReady becomes true
  useEffect(() => {
    if (isReady && !wasReadyRef.current) {
      const now = Date.now();
      lastToastTimeRef.current = now;
      lastToastPageIndexRef.current = pagesVisitedRef.current.length;
      wasReadyRef.current = true;
      console.log('[SessionProgressToast] Book loaded, starting tracking');
    }
  }, [isReady]);

  // Trigger toast
  const triggerToast = useCallback(() => {
    const concepts = selectConceptsForToast(
      pagesVisitedRef.current,
      tldrCacheRef.current,
      lastToastPageIndexRef.current,
      shownConceptsRef.current,
      currentPageRef.current
    );

    console.log('[SessionProgressToast] Triggering toast with concepts:', concepts);

    if (concepts.length < 2) {
      console.log('[SessionProgressToast] Less than 2 concepts available, skipping toast');
      return;
    }

    // Track shown concepts
    concepts.forEach(c => shownConceptsRef.current.add(c));

    // Update tracking refs
    lastToastTimeRef.current = Date.now();
    lastToastPageIndexRef.current = pagesVisitedRef.current.length;

    // Show toast
    setToastConcepts(concepts);
    setShowToast(true);
  }, []);

  // Check if we should show toast
  const checkTrigger = useCallback(() => {
    if (!wasReadyRef.current) return;
    if (showToast) return; // Already showing

    const now = Date.now();
    const timeSinceLastToast = now - lastToastTimeRef.current;
    const pagesSinceLastToast = pagesVisitedRef.current.length - lastToastPageIndexRef.current;

    console.log('[SessionProgressToast] Check:', {
      timeSinceLastToast: Math.round(timeSinceLastToast / 1000) + 's',
      pagesSinceLastToast,
      pagesVisited: pagesVisitedRef.current.length,
      tldrCacheSize: tldrCacheRef.current.size
    });

    // Trigger if 20 min elapsed OR 10 pages read (whichever first)
    if (timeSinceLastToast >= TOAST_TIME_THRESHOLD_MS || pagesSinceLastToast >= TOAST_PAGES_THRESHOLD) {
      // Need at least 2 pages visited total to show toast
      if (pagesVisitedRef.current.length >= 2) {
        triggerToast();
      }
    }
  }, [showToast, triggerToast]);

  // Set up check interval
  useEffect(() => {
    if (!isReady) return;

    checkIntervalRef.current = setInterval(checkTrigger, CHECK_INTERVAL_MS);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, [isReady, checkTrigger]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, []);

  // Auto-dismiss toast after 25 seconds if user ignores it
  useEffect(() => {
    if (showToast) {
      const autoDismissTimer = setTimeout(() => {
        console.log('[SessionProgressToast] Auto-dismissing after 25 seconds');
        setShowToast(false);
        setToastConcepts([]);
      }, 25000); // 25 seconds

      return () => clearTimeout(autoDismissTimer);
    }
  }, [showToast]);

  // Action handlers
  const onRecap = useCallback(() => {
    setShowToast(false);
    return `Give me a quick recap of ${toastConcepts.join(', ')}`;
  }, [toastConcepts]);

  const onConnect = useCallback(() => {
    setShowToast(false);
    return `How are ${toastConcepts.join(', ')} connected?`;
  }, [toastConcepts]);

  const onDismiss = useCallback(() => {
    setShowToast(false);
  }, []);

  return {
    showToast,
    toastConcepts,
    onRecap,
    onConnect,
    onDismiss
  };
}
